import {createAsyncThunk} from "@reduxjs/toolkit";
import {condition, request} from "../../res/rest/restRequest";
import {api} from "../../res/rest/api";
import axios from "axios";
import {env} from "../../res/config/env";
import {idempiereType} from "../../res/dataServices/idempiereType";
import {getBPartnerUsers} from "../../api/request/account/userRequest";
import ReactGA from 'react-ga4';
import {
    getActiveBackOrderRequest,
    getBankingRequest,
    getCreditStatusRequest
} from "../../api/request/account/bPartnerRequest";
import {idempiereProcess} from "../../res/rest/process";

const auth = (session) => {
    return {
        headers: {
            Authorization: `Bearer ` + session.authToken
        }
    }
}

const getSalesRegion = async(session, regionID) => {
    const req = request.model(api.MODEL.C_SALES_REGION)
        .expand("x_deliveryPreference")
        .filter(condition.eq("C_SalesRegion_ID", regionID))
        .buildRequest();

    const url = env.API_URL + req;
    const response = await axios.get(url, auth(session));
    return response.data.records[0];
}

const attachSalesRegionToLocations = async(session, locations) => {
    const salesRegions = {};

    for(const location of locations) {
        const regionID = location["C_SalesRegion_ID"]
        if(regionID) {
            const region = salesRegions[regionID.id] ? salesRegions[regionID.id] :
                await getSalesRegion(session, regionID.id);
            salesRegions[regionID.id] = region;
            location["C_SalesRegion_ID"] = region;
        }
    }
}

const getLocations = async(session) => {
    const req = request.model(api.MODEL.BP_LOCATION)
        .expand("C_Location_ID", "C_SalesRegion_ID")
        .filter(condition.eq("C_BPartner_ID", session.bPartnerID))
        .buildRequest();

    const url = env.API_URL + req;
    const response = await axios.get(url, auth(session));
    const locations = response.data.records;
    await attachSalesRegionToLocations(session, locations)

   return locations;
}

const createAddress = async(session, address) => {
    const req = request.model(api.MODEL.C_LOCATION).buildRequest();
    const body = {
        "address1": address.address1,
        "address2": address.address2,
        "city": address.city,
        "postal": address.postal,
        "C_Country_ID": 333
    }

    const response = await axios.post(env.API_URL + req, body, api.auth(session));
    return response.data;
}

const createBPLocation = async(session, address, location) => {
    const req = request.model(api.MODEL.BP_LOCATION).buildRequest();
    const body = {
        "name": address.addressName,
        "phone": address.phone,
        "C_BPartner_ID": session.bPartnerID,
        "C_Location_ID": location.id,
        "isBillTo": "Y",
        "isShipTo": "Y",
        "isPayFrom": "Y",
        "isRemitTo": "Y"
    }

    const response = await axios.post(env.API_URL + req, body, api.auth(session));
    return response.data;
}

const getJustTheOrders = async(session, conditions, limit) => {
    const req = request.model("web_ordering_order_v")
        .filter(condition.eq("c_bpartner_id", session.bPartnerID))
        .orderBy("Created DESC")
        .hostURL(env.API_URL);

    if(limit) req.top(limit);

    if(conditions && conditions.length > 0) conditions.forEach(c => req.filter(c));

    const resp = await axios.get(req.buildRequest(), api.auth(session));

    return api.recordsToMap(resp.data.records);
}

const getJustTheInvoices = async(session, conditions, includeProducts, limit) => {

    const lineReq = request.subQueryList(api.MODEL.WEB_INVOICE_LINE);

    const req = request.model(api.MODEL.C_INVOICE)
        .filter(condition.eq("c_bpartner_id", session.bPartnerID),
            condition.eq("isSoTrx", "true"))
        .expand(lineReq)
        .orderBy("Created DESC")
        .hostURL(env.API_URL);

    if(limit) {
        req.top(limit);
    }

    if(conditions && conditions.length > 0)
        conditions.forEach(c => req.filter(c));
    if(includeProducts)
        lineReq.expand("M_Product_ID","C_Uom_ID","C_Charge_ID");

    const resp = await axios.get(req.buildRequest(), api.auth(session));
    return api.recordsToMap(resp.data.records);
}

const getJustTheShipments = async(session, conditions, includeProducts, limit) => {

    const lineReq = request.subQueryList(api.MODEL.WEB_SHIPMENT_LINE);

    const req = request.model(api.MODEL.M_INOUT)
        .filter(condition.eq("isSoTrx", "true"))
        .filter(condition.eq("c_bpartner_id", session.bPartnerID))
        .filter(condition.eq("C_DocType_ID", 1000011))
        .orderBy("Created DESC")
        .expand(lineReq)
        .hostURL(env.API_URL);

    if(limit) {
        req.top(limit);
    }

    if(conditions && conditions.length > 0) conditions.forEach(c => req.filter(c));
    if(includeProducts) lineReq.expand("M_Product_ID", "C_UOM_ID", "C_Charge_ID");

    const resp = await axios.get(req.buildRequest(), api.auth(session));
    return api.recordsToMap(resp.data.records);
}

const getJustTheActiveShipments = async(session, conditions, includeProducts) => {
    const lineReq = request.subQueryList(api.MODEL.WEB_SHIPMENT_LINE);

    const req = request.model("web_active_shipment_v")
        .filter(condition.eq("c_bpartner_ID", session.bPartnerID))
        .expand("c_bpartner_location_ID")
        .expand("x_routeLines")
        .expand(lineReq)
        .hostURL(env.API_URL);

    if(conditions && conditions.length > 0) conditions.forEach(c => req.filter(c));

    if(includeProducts) lineReq.expand("M_Product_ID", "C_UOM_ID", "C_Charge_ID");
    const resp = await axios.get(req.buildRequest(), api.auth(session));
    return resp.data.records;
}

const getShipmentLinks = async (session, shipmentList) => {

    const invoiceIDs = new Set();
    const orderIDs = new Set();

    for(const shipment of shipmentList) {
        const lines = shipment["web_ordering_shipmentLine_v"]
        if(lines) {
            for(const line of lines) {
                console.log("l", line)
                if(line.lineinvoices) {
                    const parsedArray = idempiereType.parseArray(line.lineinvoices);
                    invoiceIDs.add(...parsedArray)
                }
                if(line["C_Order_ID"]) {
                    orderIDs.add(line["C_Order_ID"].id)
                }
            }
        }
    }

    console.log("invIDs", invoiceIDs);
    const links = {};
    if(invoiceIDs.size > 0) {
        const conditions = [ condition.in("c_invoice_ID", ...invoiceIDs) ];
        links.invoices = await getJustTheInvoices(session, conditions);
    }
    if(orderIDs.size > 0) {
        const conditions = [ condition.in("c_order_ID", ...orderIDs) ];
        links.orders = await getJustTheOrders(session, conditions);
    }
    return links;
}

export const getBPLocations = createAsyncThunk(
    'account/getBPAddress',
    async({session}, {rejectWithValue, dispatch}) => {

        try {
            const locations = await getLocations(session);
            return { locations };
        } catch (error) {
            return api.catchError(dispatch, rejectWithValue, error, session);
        }

    }
)

export const createLocation = createAsyncThunk(
    'account/addLocation',
    async({session, addressInput}, {rejectWithValue, dispatch}) => {
        try {

            const address = await createAddress(session, addressInput);
            const bpLocation = await createBPLocation(session, addressInput, address)
            bpLocation["C_Location_ID"] = address;
            await attachSalesRegionToLocations(session, [bpLocation]);
            return { bpLocation };

        } catch (error) {
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const getOrders = createAsyncThunk(
    "account/getOrders",
    async({session, conditions, limit}, {rejectWithValue, dispatch}) => {
        try {
            const orders = await getJustTheOrders(session, conditions, limit);
            const orderingDetails = {orders};

            const orderLinks = {};

            const shipments = new Set();
            const invoices = new Set();

                    // Log the view_orders event to GA4
                ReactGA.event({
                    category: 'my_account',
                    action: 'view_my_orders',
                    label: 'User viewed their orders',
                    value: orders.length, 
    
                });

            Object.values(orders).forEach((o) => {
                if(o.ordershipments) {
                    const shipmentIDs = idempiereType.parseArray(o.ordershipments);
                    orderLinks[o.id] = shipmentIDs;
                    shipments.add(...shipmentIDs);
                }
                if(o.orderinvoices) {
                    const invoiceIDs = idempiereType.parseArray(o.orderinvoices);
                    invoices.add(...invoiceIDs);
                }
            });

            if(shipments.size > 0) {
                const conditions = [condition.in("m_inOut_ID", ...shipments)]
                orderingDetails.shipments = await getJustTheShipments(session, conditions)
            }
            if(invoices.size > 0) {
                const conditions = [condition.in("c_invoice_ID", ...invoices)]
                orderingDetails.invoices = await getJustTheInvoices(session, conditions)
            }

            return orderingDetails;
        } catch (error) {
            console.log("error", error)
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const getInvoices = createAsyncThunk(
    "account/getInvoice",
    async ({session, conditions, includeProducts, limit}, {rejectWithValue, dispatch}) => {
        try {
            const invoices = await getJustTheInvoices(session, conditions, includeProducts, limit);
            const ordering = {invoices};

            const shipmentIDs = new Set();
            const orderIDs = new Set();

             // Log the view_orders event to GA4
             ReactGA.event({
                category: 'my_account',
                action: 'view_my_invoices',
                label: 'User viewed their invoices',
                value: invoices.length, 

            });

            Object.values(invoices).forEach((inv) => {
                const lines = inv.web_ordering_invoiceLine_v;
                if(lines) {
                    for(const line of lines) {
                        const shipment = line["M_InOut_ID"];
                        const order = line["C_Order_ID"];

                        if (shipment) shipmentIDs.add(shipment.id);
                        if (order) orderIDs.add(order.id);
                    }
                }
            });


            if(shipmentIDs.size > 0) {
                const conditions = [condition.in("m_inOut_ID", ...shipmentIDs)]
                ordering.shipments = await getJustTheShipments(session, conditions)
            }
            if(orderIDs.size > 0) {
                const conditions = [condition.in("c_order_ID", ...orderIDs)]
                ordering.orders = await getJustTheOrders(session, conditions)
            }

            return ordering;
        } catch (error) {
            console.log("error", error)
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const getShipments = createAsyncThunk(
    "account/getShipment",
    async({session, conditions, includeProducts, limit}, {rejectWithValue, dispatch}) => {
        try {
            const shipments = await getJustTheShipments(session, conditions, includeProducts, limit);

                // Log the view_orders event to GA4
                ReactGA.event({
                    category: 'my_account',
                    action: 'view_my_shipments',
                    label: 'User viewed their shipments',
                    value: shipments.length, 
    
                });
            const links = await getShipmentLinks(session, Object.values(shipments));
            return {shipments, ...links};
        } catch (error) {
            console.log("error", error);
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const getOrderLines = createAsyncThunk(
    "account/getOrderLines",
    async ({session, orderID}, {rejectWithValue, dispatch}) => {
        try {

            const req = request.model("web_ordering_orderline_v")
                .filter(condition.eq("c_bpartner_id", session.bPartnerID))
                .filter(condition.eq("c_order_id", orderID))
                .expand("M_Product_ID", "C_UOM_ID", "C_Charge_ID")
                .hostURL(env.API_URL)
                .buildRequest();

            const resp = await axios.get(req, api.auth(session));

            const orderLines = resp.data.records;
            orderLines.sort((a,b) => a.Line - b.Line);
            return {orderID, orderLines};

        } catch (error) {
            console.log("error", error)
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const getActiveDeliveries = createAsyncThunk(
    "account/activeDeliveries",
    async ({session, conditions, includeProducts}, {rejectWithValue, dispatch}) => {
        try {
            const shipments = await getJustTheActiveShipments(session, conditions, includeProducts);
            const links = await getShipmentLinks(session, Object.values(shipments));
            return {shipments, ...links}
        } catch (error) {
            console.log("error", error)
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const getColleagues = createAsyncThunk(
    "account/colleagues",
    async({ session }, {rejectWithValue, dispatch}) => {
        try {
            const req = getBPartnerUsers(session.bPartnerID)
                .buildRequest(env.API_URL);

            const response = await axios.get(req, api.auth(session));
            return { colleagues: response.data.records };
        } catch (error) {
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const getBanking = createAsyncThunk(
    "account/banking",
    async({ session }, {rejectWithValue, dispatch}) => {
        try {
            const req = getBankingRequest(session.bPartnerID)
                .buildRequest(env.API_URL);

            const response = await axios.get(req, api.auth(session));
            return {banking: response.data.records};
        } catch (error) {
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const getCreditStatus = createAsyncThunk(
    "account/creditStatus",
    async({ session }, {rejectWithValue, dispatch}) => {
        try {
            const req = getCreditStatusRequest(session.bPartnerID)
                .buildRequest(env.API_URL);

            const response = await axios.get(req, api.auth(session));
            return {creditStatus: response.data.records[0]};
        } catch (error) {
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const getActiveBackOrders = createAsyncThunk(
    "account/backOrders",
    async ({ session }, { rejectWithValue, dispatch }) => {
        try {
            const req = getActiveBackOrderRequest(session.bPartnerID)
                .buildRequest(env.API_URL);

            const response = await axios.get(req, api.auth(session));
            return { backOrderLines: response.data.records }
        } catch (error) {
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const cancelBackOrder = createAsyncThunk(
    "account/cancelBackOrder",
    async ({ session, orderLineID }, { rejectWithValue, dispatch }) => {
        try {

            const req = request.process(api.PROCESS.BACK_ORDER_CANCEL)
                .param("C_OrderLine_ID", orderLineID);
            const url = env.API_URL + req.buildRequest();
            const resp = await axios.post(url, req.buildRequestBody(), api.auth(session));
            const data = idempiereProcess.handleResponseThrowingException(resp);
            return {orderLineID, response: data.summary};

        } catch (error) {
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)

export const updateLocation = createAsyncThunk(
    "account/updateLocation",
    async ({ session, locationID, updates },  { rejectWithValue, dispatch }) => {
        try {
            const url = request.model(api.MODEL.BP_LOCATION)
                .id(locationID)
                .buildRequest(env.API_URL);
            const response = await axios.put(url, updates, api.auth(session));
            return { updatedLoc: response.data }
        } catch (error) {
            return api.catchError(dispatch, rejectWithValue, error, session);
        }
    }
)
